import { apiClient } from './api-client';
import type { EvidenceSelectionItem } from './authorization-case.service';

export type InspectionStatus =
  | 'UNASSIGNED'
  | 'SCHEDULED'
  | 'ASSIGNED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'REVIEWED'
  | 'CLOSED'
  | 'CANCELLED'
  | 'TRANSFER_REQUESTED';

export type InspectionWarrantyStatus =
  'PENDING_VERIFICATION' | 'UNDER_WARRANTY' | 'OUT_OF_WARRANTY' | 'NO_WARRANTY';

export type OperationsReviewStatus =
  'PENDING' | 'RETURNED' | 'REVIEWED' | 'APPROVED_FOR_COMMERCIAL';

export type CommercialReviewStatus = 'PENDING' | 'SENT_TO_CUSTOMER' | 'RETURNED_TO_OPERATIONS';

export type CustomerApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'MODIFICATION_REQUESTED';

export type InspectionFindingSeverity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
export type InspectionAttachmentType = 'PHOTO' | 'DOCUMENT' | 'VIDEO';
/** D-129 — Commercial's review of the customer-submitted Work Order. */
export type WorkOrderReviewStatus = 'PENDING_COMMERCIAL_REVIEW' | 'ACCEPTED';

/** D-129 — `GET /inspections/:id/commercial` subset the detail page needs:
 * AME-signed download URLs (present only with inspection:download-commercial-pdf). */
export interface CommercialDocumentLinks {
  quotationDocumentId: string | null;
  quotationDocumentName: string | null;
  quotationDocumentUrl: string | null;
  workOrderDocumentId: string | null;
  workOrderDocumentName: string | null;
  workOrderDocumentUrl: string | null;
}

export interface Inspection {
  id: string;
  tenantId: string;
  inspectionNumber: string;
  intakeId: string | null;
  equipmentId: string;
  customerId: string | null;
  customerLocationId: string | null;
  serviceTypeId: string | null;
  priorityId: string | null;
  assignedEngineerId: string | null;
  assignedByUserId: string | null;
  assignedAt: string | null;
  scheduledAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  reviewedAt: string | null;
  closedAt: string | null;
  cancelledAt: string | null;
  status: InspectionStatus;
  warrantyStatus: InspectionWarrantyStatus;
  warrantyTypeId: string | null;
  warrantyTypeName: string | null;
  customerRequestedScope: string | null;
  operationsReviewStatus: OperationsReviewStatus;
  operationsRecommendation?: string | null;
  operationsReturnReason: string | null;
  operationsReviewedByUserId: string | null;
  operationsReviewedAt: string | null;
  estimatedPartsCount: number;
  totalFindingsCount: number;
  batchReferenceNo: string | null;
  notes: string | null;
  createdByUserId: string | null;
  updatedByUserId: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  // D-021: present only when caller has inspection:view-commercial
  commercialReviewStatus?: CommercialReviewStatus | null;
  commercialReviewedByUserId?: string | null;
  commercialReviewedAt?: string | null;
  commercialSentAt?: string | null;
  commercialSummary?: string | null;
  commercialReturnReason?: string | null;
  quotationReference?: string | null;
  quotationDocumentId?: string | null;
  quotationDocumentName?: string | null;
  // D-129 — the document pinned at the last send (what the customer sees)
  quotationSentDocumentId?: string | null;
  quotationSentDocumentName?: string | null;
  customerApprovalStatus?: CustomerApprovalStatus | null;
  customerApprovalRespondedAt?: string | null;
  customerModificationReason?: string | null;
  /**
   * Request Thread Stage B — the job's OPEN modification round, or null.
   *
   * Behind the same commercial gate as `customerModificationReason`, because
   * `latestCustomerRequestText` is the customer's own words. Absent entirely
   * for a role without `inspection:view-commercial`.
   */
  openModificationRound?: OpenModificationRoundSummary | null;
  // D-129 — customer Work Order layer (same D-021 gate)
  customerApprovalContactName?: string | null;
  workOrderNumber?: string | null;
  workOrderDescription?: string | null;
  workOrderDocumentId?: string | null;
  workOrderDocumentName?: string | null;
  workOrderReviewStatus?: WorkOrderReviewStatus | null;
  workOrderAcceptedAt?: string | null;
  workOrderAcceptedByName?: string | null;
  // Phase 11.X — Ownership fields
  pendingTransferToId?: string | null;
  pendingTransferReason?: string | null;
  pendingTransferToName?: string | null;
  pendingTakeoverFromId?: string | null;
  pendingTakeoverReason?: string | null;
  pendingTakeoverFromName?: string | null;
  // Enriched from relations
  equipmentName: string;
  equipmentAssetNumber: string;
  equipmentSerialNumber: string;
  equipmentManufacturerName: string | null;
  equipmentModelName: string | null;
  intakeNumber: string | null;
  intakeComplaintDescription: string | null;
  intakeReceivedAt: string | null;
  /** Customer CONTACT / person name — not the organization. */
  customerName: string | null;
  /**
   * Customer ORGANIZATION name — the authoritative customer identity for the
   * inspection workflow. Nullable, because `Customer.organizationName` is
   * optional in the schema.
   */
  customerOrganizationName: string | null;
  customerLocationName: string | null;
  serviceTypeName: string | null;
  priorityName: string | null;
  assignedEngineerName: string | null;
  assignedByName: string | null;
  operationsReviewedByName: string | null;
  commercialReviewedByName?: string | null;
  createdByName: string | null;
  // Work Queue display fields — computed server-side, read-only
  currentStage: WorkQueueStage;
  daysWaiting: number;
}

export type WorkQueueStage =
  InspectionStatus | 'RETURNED' | 'WAITING_COMMERCIAL' | 'WAITING_CUSTOMER';

export interface Finding {
  id: string;
  tenantId: string;
  inspectionId: string;
  findingNumber: number;
  severity: InspectionFindingSeverity;
  componentArea: string | null;
  title: string;
  description: string;
  insideRequestedScope: boolean;
  warrantyApplicable: boolean;
  isCommercialReview?: boolean;
  berCategory?: string | null;
  berReason?: string | null;
  recommendedAction: string | null;
  recommendedPartsSummary: string | null;
  estimatedRepairHours: string | null;
  sortOrder: number;
  createdByName: string | null;
  updatedByName: string | null;
  createdAt: string;
  updatedAt: string;
  // Lightweight summary counts shipped with each finding for accordion header display
  photoCount: number;
  videoCount: number;
  documentCount: number;
  noteCount: number;
  authorizationStatus: string | null;
}

export interface Attachment {
  id: string;
  tenantId: string;
  inspectionId: string;
  findingId: string | null;
  attachmentType: InspectionAttachmentType;
  originalFileName: string;
  mimeType: string;
  fileSizeBytes: string;
  url: string | null;
  thumbnailUrl: string | null;
  isCommercialDocument?: boolean;
  caption: string | null;
  sortOrder: number;
  uploadedByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Note {
  id: string;
  tenantId: string;
  inspectionId: string;
  authorId: string;
  authorName: string;
  authorRoleName: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

export interface NoteListResponse {
  data: Note[];
  total: number;
  page: number;
  pageSize: number;
  totalPages?: number;
}

export interface FindingNote {
  id: string;
  tenantId: string;
  inspectionId: string;
  findingId: string;
  authorId: string;
  authorName: string;
  authorRoleName: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

export interface FindingNoteListResponse {
  data: FindingNote[];
  total: number;
  page: number;
  pageSize: number;
  totalPages?: number;
}

export interface AuthorizationHistoryItem {
  id: string;
  actorId: string;
  actorName: string;
  actorRoleName: string;
  action: string;
  comment: string | null;
  createdAt: string;
}

export interface Authorization {
  id: string;
  tenantId: string;
  inspectionId: string;
  findingId: string | null;
  requestedById: string;
  requestedByName: string;
  respondedById: string | null;
  respondedByName: string | null;
  reason: string;
  responseNotes: string | null;
  status: string;
  workflowStage: string;
  approvalRequestTypeId: string | null;
  approvalRequestTypeName: string | null;
  isCustom: boolean;
  customApprovalName: string | null;
  requestedAt: string;
  respondedAt: string | null;
  createdAt: string;
  updatedAt: string;
  history: AuthorizationHistoryItem[];
}

export interface AuthorizationListResponse {
  data: Authorization[];
  total: number;
  page: number;
  pageSize: number;
  totalPages?: number;
}

/** Mirrors the server's UpdateInspectionDto — header fields only. */
export interface UpdateInspectionPayload {
  priorityId?: string;
  assignedEngineerId?: string;
  warrantyStatus?: InspectionWarrantyStatus;
  warrantyTypeId?: string;
  batchReferenceNo?: string;
}

/**
 * Per-tab inspection totals. The five buckets are DISJOINT — `inProgress`
 * excludes returned inspections, which are reported by `returned`.
 */
export interface InspectionStatusCounts {
  assigned: number;
  inProgress: number;
  transferPending: number;
  returned: number;
  completed: number;
}

export interface InspectionListResponse {
  data: Inspection[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface FindingListResponse {
  data: Finding[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Engineer-Controlled Evidence Selection (docs/reports/authorization/
// AUTHORIZATION_EVIDENCE_SELECTION_IMPLEMENTATION) — candidates for the evidence
// picker shown when creating/resubmitting an Authorization Request. Bucketed from
// existing inspection data only — nothing here is newly created.
export interface EvidenceCandidateItem {
  id: string;
  label: string;
  thumbnailUrl: string | null;
}

export interface EvidenceCandidatesResponse {
  inspectionPhotos: EvidenceCandidateItem[];
  findingPhotos: EvidenceCandidateItem[];
  videos: EvidenceCandidateItem[];
  documents: EvidenceCandidateItem[];
  procedureEvidence: EvidenceCandidateItem[];
  findings: EvidenceCandidateItem[];
  notes: EvidenceCandidateItem[];
}

export interface AttachmentListResponse {
  data: Attachment[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export type OwnershipAction =
  | 'TRANSFER_INITIATED'
  | 'TRANSFER_ACCEPTED'
  | 'TRANSFER_REJECTED'
  | 'TRANSFER_REVOKED'
  | 'TAKEOVER_REQUESTED'
  | 'TAKEOVER_ACCEPTED'
  | 'TAKEOVER_REJECTED'
  | 'FORCE_TRANSFER';

export interface OwnershipHistoryItem {
  id: string;
  inspectionId: string;
  action: OwnershipAction;
  oldOwnerId: string | null;
  oldOwnerName: string | null;
  newOwnerId: string | null;
  newOwnerName: string | null;
  initiatorId: string;
  initiatorName: string;
  reason: string;
  comment: string | null;
  ipAddress: string | null;
  createdAt: string;
}

export interface OwnershipHistoryResponse {
  data: OwnershipHistoryItem[];
  total: number;
}

export interface PendingTransferItem {
  id: string;
  inspectionNumber: string;
  pendingTransferReason: string | null;
  assignedEngineerName: string | null;
  assignedAt: string | null;
}

export interface PendingTakeoverItem {
  id: string;
  inspectionNumber: string;
  pendingTakeoverReason: string | null;
  takeoverRequesterName: string | null;
}

/**
 * B6.19 — the backend's answer to "may I offer a Close button?".
 * `reason` is a stable code, never a sentence: the UI owns the wording.
 */
export interface ClosureEligibility {
  eligible: boolean;
  reason:
    | 'ALREADY_TERMINAL'
    | 'REPORT_UNDER_REVIEW'
    | 'NOT_REVIEWED_AND_REPORT_NOT_APPROVED'
    | 'AWAITING_CUSTOMER_DECISION'
    | null;
}

export const INSPECTION_STATUS_LABELS: Record<InspectionStatus, string> = {
  UNASSIGNED: 'Unassigned',
  SCHEDULED: 'Scheduled',
  ASSIGNED: 'Assigned',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  REVIEWED: 'Reviewed',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
  TRANSFER_REQUESTED: 'Transfer Requested',
};

export const WORK_QUEUE_STAGE_LABELS: Record<WorkQueueStage, string> = {
  ...INSPECTION_STATUS_LABELS,
  RETURNED: 'Returned to Engineer',
  WAITING_COMMERCIAL: 'Waiting for Commercial',
  WAITING_CUSTOMER: 'Waiting Customer Decision',
};

export const WARRANTY_STATUS_LABELS: Record<InspectionWarrantyStatus, string> = {
  PENDING_VERIFICATION: 'Pending Verification',
  UNDER_WARRANTY: 'Under Warranty',
  OUT_OF_WARRANTY: 'Out of Warranty',
  NO_WARRANTY: 'No Warranty',
};

export const OPS_REVIEW_STATUS_LABELS: Record<OperationsReviewStatus, string> = {
  PENDING: 'Pending',
  RETURNED: 'Returned',
  REVIEWED: 'Reviewed',
  APPROVED_FOR_COMMERCIAL: 'Approved for Commercial',
};

export const COMMERCIAL_STATUS_LABELS: Record<CommercialReviewStatus, string> = {
  PENDING: 'Pending',
  SENT_TO_CUSTOMER: 'Sent to Customer',
  RETURNED_TO_OPERATIONS: 'Returned to Operations',
};

export const CUSTOMER_APPROVAL_LABELS: Record<CustomerApprovalStatus, string> = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  MODIFICATION_REQUESTED: 'Modification Requested',
};
export const WORK_ORDER_REVIEW_LABELS: Record<WorkOrderReviewStatus, string> = {
  PENDING_COMMERCIAL_REVIEW: 'Awaiting Commercial Acceptance',
  ACCEPTED: 'Accepted by Commercial',
};

export const FINDING_SEVERITY_LABELS: Record<InspectionFindingSeverity, string> = {
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
};

export const ATTACHMENT_TYPE_LABELS: Record<InspectionAttachmentType, string> = {
  PHOTO: 'Photo',
  DOCUMENT: 'Document',
  VIDEO: 'Video',
};

export const inspectionService = {
  list: (params: {
    page?: number;
    pageSize?: number;
    search?: string;
    status?: InspectionStatus;
    warrantyStatus?: InspectionWarrantyStatus;
    operationsReviewStatus?: OperationsReviewStatus;
    /** Excludes a review status. Ignored by the API if the positive filter is also set. */
    excludeOperationsReviewStatus?: OperationsReviewStatus;
    commercialReviewStatus?: string;
    customerApprovalStatus?: string;
    /** D-131 (G-1b) — jobs with an OPEN modification round in this state. */
    modificationRoundState?: string;
    equipmentId?: string;
    customerId?: string;
    assignedEngineerId?: string;
    intakeId?: string;
    inspectionNumber?: string;
    priorityId?: string;
    departmentId?: string;
    receivedFrom?: string;
    receivedTo?: string;
    sortBy?:
      | 'inspectionNumber'
      | 'receivedAt'
      | 'customerName'
      | 'assignedEngineerName'
      | 'priority'
      | 'status'
      | 'updatedAt';
    sortDir?: 'asc' | 'desc';
  }): Promise<InspectionListResponse> =>
    apiClient.get<InspectionListResponse>('/inspections', { params }).then((r) => r.data),

  /**
   * Per-tab totals in ONE request.
   *
   * The list endpoint's `total` covers only the filter it was called with, so
   * tab counts cannot be derived from it without one request per tab. Scoping
   * is applied server-side and mirrors the list endpoint exactly.
   */
  statusCounts: (params?: { assignedEngineerId?: string }): Promise<InspectionStatusCounts> =>
    apiClient
      .get<InspectionStatusCounts>('/inspections/status-counts', { params })
      .then((r) => r.data),

  get: (id: string): Promise<Inspection> =>
    apiClient.get<Inspection>(`/inspections/${id}`).then((r) => r.data),

  /**
   * Header-field update — the existing PATCH /inspections/:id, gated on
   * `inspection:update`.
   *
   * This is the ONLY route by which the intake screen changes priority.
   * Priority is an Inspection field: it is not on EquipmentIntake and must not
   * be added there, because the same machine can be re-inspected at a different
   * urgency without its intake changing at all.
   */
  update: (id: string, payload: UpdateInspectionPayload): Promise<Inspection> =>
    apiClient.patch<Inspection>(`/inspections/${id}`, payload).then((r) => r.data),

  getFindings: (
    inspectionId: string,
    params?: { page?: number; pageSize?: number },
  ): Promise<FindingListResponse> =>
    apiClient
      .get<FindingListResponse>(`/inspections/${inspectionId}/findings`, { params })
      .then((r) => r.data),

  getAuthorizationEvidenceCandidates: (inspectionId: string): Promise<EvidenceCandidatesResponse> =>
    apiClient
      .get<EvidenceCandidatesResponse>(
        `/inspections/${inspectionId}/authorization-evidence-candidates`,
      )
      .then((r) => r.data),

  getAttachments: (
    inspectionId: string,
    params?: {
      page?: number;
      pageSize?: number;
      attachmentType?: InspectionAttachmentType;
      findingId?: string;
    },
  ): Promise<AttachmentListResponse> =>
    apiClient
      .get<AttachmentListResponse>(`/inspections/${inspectionId}/attachments`, { params })
      .then((r) => r.data),

  submit: (id: string): Promise<Inspection> =>
    apiClient.post<Inspection>(`/inspections/${id}/submit`).then((r) => r.data),

  // ── B6.19 — service job lifecycle. The endpoints shipped in B6.17 with no
  // client method and no button, so a job could only be terminated over raw
  // HTTP (B6.18 finding U-1). These three close that gap; the backend guards,
  // events, audit and realtime are unchanged.
  closureEligibility: (id: string): Promise<ClosureEligibility> =>
    apiClient.get<ClosureEligibility>(`/inspections/${id}/closure-eligibility`).then((r) => r.data),

  close: (id: string, closureReason?: string): Promise<Inspection> =>
    apiClient
      .post<Inspection>(`/inspections/${id}/close`, closureReason ? { closureReason } : {})
      .then((r) => r.data),

  cancel: (id: string, cancellationReason: string): Promise<Inspection> =>
    apiClient
      .post<Inspection>(`/inspections/${id}/cancel`, { cancellationReason })
      .then((r) => r.data),

  acknowledgeCommercialReturn: (id: string): Promise<Inspection> =>
    apiClient
      .post<Inspection>(`/inspections/${id}/operations/acknowledge-return`)
      .then((r) => r.data),

  operationsApprove: (id: string): Promise<Inspection> =>
    apiClient.post<Inspection>(`/inspections/${id}/operations/approve`).then((r) => r.data),

  operationsReturn: (id: string, returnReason: string): Promise<Inspection> =>
    apiClient
      .post<Inspection>(`/inspections/${id}/operations/return`, { returnReason })
      .then((r) => r.data),

  commercialSend: (
    id: string,
    payload: { commercialSummary: string; quotationReference: string },
  ): Promise<Inspection> =>
    apiClient.post<Inspection>(`/inspections/${id}/commercial/send`, payload).then((r) => r.data),

  commercialReturn: (id: string, returnReason: string): Promise<Inspection> =>
    apiClient
      .post<Inspection>(`/inspections/${id}/commercial/return`, { returnReason })
      .then((r) => r.data),

  // D-129 — quotation PDF gate (Commercial). Multipart; PDF only.
  attachQuotationDocument: (id: string, file: File): Promise<Inspection> => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient
      .post<Inspection>(`/inspections/${id}/commercial/quotation-document`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },
  removeQuotationDocument: (id: string): Promise<Inspection> =>
    apiClient
      .delete<Inspection>(`/inspections/${id}/commercial/quotation-document`)
      .then((r) => r.data),
  /** D-129 — AME-signed download links for the quotation / Work Order PDFs. */
  getCommercialDocumentLinks: (id: string): Promise<CommercialDocumentLinks> =>
    apiClient.get<CommercialDocumentLinks>(`/inspections/${id}/commercial`).then((r) => r.data),
  // D-129 — Commercial accepts the customer-submitted Work Order.
  acceptWorkOrder: (id: string): Promise<Inspection> =>
    apiClient
      .post<Inspection>(`/inspections/${id}/commercial/work-order/accept`)
      .then((r) => r.data),
  // D-129 — staff-recorded customer approval carries the Work Order (multipart).
  customerApprove: (
    id: string,
    payload: { workOrderNumber: string; workOrderDescription: string; file: File },
  ): Promise<Inspection> => {
    const fd = new FormData();
    fd.append('workOrderNumber', payload.workOrderNumber);
    fd.append('workOrderDescription', payload.workOrderDescription);
    fd.append('workOrderDocument', payload.file);
    return apiClient
      .post<Inspection>(`/customer-approvals/${id}/approve`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  customerReject: (id: string): Promise<Inspection> =>
    apiClient.post<Inspection>(`/customer-approvals/${id}/reject`).then((r) => r.data),

  customerRequestModification: (id: string, modificationReason: string): Promise<Inspection> =>
    apiClient
      .post<Inspection>(`/customer-approvals/${id}/request-modification`, { modificationReason })
      .then((r) => r.data),

  start: (id: string): Promise<Inspection> =>
    apiClient.post<Inspection>(`/inspections/${id}/start`).then((r) => r.data),

  getJobPool: (params?: {
    page?: number;
    pageSize?: number;
    search?: string;
  }): Promise<InspectionListResponse> =>
    apiClient.get<InspectionListResponse>('/inspections/job-pool', { params }).then((r) => r.data),

  claim: (id: string): Promise<Inspection> =>
    apiClient.post<Inspection>(`/inspections/${id}/claim`).then((r) => r.data),

  unclaim: (id: string): Promise<Inspection> =>
    apiClient.post<Inspection>(`/inspections/${id}/unclaim`).then((r) => r.data),

  transfer: (id: string, targetEngineerId: string): Promise<Inspection> =>
    apiClient
      .post<Inspection>(`/inspections/${id}/transfer`, { targetEngineerId })
      .then((r) => r.data),

  createFinding: (
    inspectionId: string,
    payload: {
      severity: InspectionFindingSeverity;
      title: string;
      description: string;
      componentArea?: string;
      insideRequestedScope?: boolean;
      warrantyApplicable?: boolean;
      recommendedAction?: string;
      recommendedPartsSummary?: string;
      estimatedRepairHours?: string;
    },
  ): Promise<Finding> =>
    apiClient.post<Finding>(`/inspections/${inspectionId}/findings`, payload).then((r) => r.data),

  updateFinding: (
    inspectionId: string,
    findingId: string,
    payload: Partial<{
      severity: InspectionFindingSeverity;
      title: string;
      description: string;
      componentArea: string;
      insideRequestedScope: boolean;
      warrantyApplicable: boolean;
      recommendedAction: string;
      recommendedPartsSummary: string;
      estimatedRepairHours: string;
    }>,
  ): Promise<Finding> =>
    apiClient
      .patch<Finding>(`/inspections/${inspectionId}/findings/${findingId}`, payload)
      .then((r) => r.data),

  deleteFinding: (inspectionId: string, findingId: string): Promise<void> =>
    apiClient.delete(`/inspections/${inspectionId}/findings/${findingId}`).then(() => undefined),

  uploadAttachment: (inspectionId: string, formData: FormData): Promise<Attachment> =>
    apiClient
      .post<Attachment>(`/inspections/${inspectionId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data),

  deleteAttachment: (inspectionId: string, attachmentId: string): Promise<void> =>
    apiClient
      .delete(`/inspections/${inspectionId}/attachments/${attachmentId}`)
      .then(() => undefined),

  replaceAttachment: (
    inspectionId: string,
    attachmentId: string,
    formData: FormData,
  ): Promise<Attachment> =>
    apiClient
      .post<Attachment>(
        `/inspections/${inspectionId}/attachments/${attachmentId}/replace`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      )
      .then((r) => r.data),

  getNotes: (
    inspectionId: string,
    params?: { page?: number; pageSize?: number },
  ): Promise<NoteListResponse> =>
    apiClient
      .get<NoteListResponse>(`/inspections/${inspectionId}/notes`, { params })
      .then((r) => r.data),

  createNote: (inspectionId: string, content: string): Promise<Note> =>
    apiClient.post<Note>(`/inspections/${inspectionId}/notes`, { content }).then((r) => r.data),

  getFindingNotes: (
    inspectionId: string,
    findingId: string,
    params?: { page?: number; pageSize?: number },
  ): Promise<FindingNoteListResponse> =>
    apiClient
      .get<FindingNoteListResponse>(`/inspections/${inspectionId}/findings/${findingId}/notes`, {
        params,
      })
      .then((r) => r.data),

  createFindingNote: (
    inspectionId: string,
    findingId: string,
    content: string,
  ): Promise<FindingNote> =>
    apiClient
      .post<FindingNote>(`/inspections/${inspectionId}/findings/${findingId}/notes`, { content })
      .then((r) => r.data),

  getAuthorizations: (
    inspectionId: string,
    findingId: string,
    params?: { page?: number; pageSize?: number },
  ): Promise<AuthorizationListResponse> =>
    apiClient
      .get<AuthorizationListResponse>(
        `/inspections/${inspectionId}/findings/${findingId}/authorizations`,
        { params },
      )
      .then((r) => r.data),

  createAuthorization: (
    inspectionId: string,
    findingId: string,
    payload: {
      reason: string;
      approvalRequestTypeId?: string;
      isCustom?: boolean;
      customApprovalName?: string;
      evidence?: EvidenceSelectionItem[];
    },
  ): Promise<Authorization> =>
    apiClient
      .post<Authorization>(
        `/inspections/${inspectionId}/findings/${findingId}/authorizations`,
        payload,
      )
      .then((r) => r.data),

  respondAuthorization: (
    inspectionId: string,
    findingId: string,
    authorizationId: string,
    payload: { action: 'APPROVED' | 'REJECTED' | 'RETURNED'; comment?: string },
  ): Promise<Authorization> =>
    apiClient
      .patch<Authorization>(
        `/inspections/${inspectionId}/findings/${findingId}/authorizations/${authorizationId}/respond`,
        payload,
      )
      .then((r) => r.data),

  resubmitAuthorization: (
    inspectionId: string,
    findingId: string,
    authorizationId: string,
    payload: { updatedReason?: string; evidence?: EvidenceSelectionItem[] },
  ): Promise<Authorization> =>
    apiClient
      .patch<Authorization>(
        `/inspections/${inspectionId}/findings/${findingId}/authorizations/${authorizationId}/resubmit`,
        payload,
      )
      .then((r) => r.data),

  // ── Phase 11.X — Ownership Management ────────────────────────────────────

  initiateTransfer: (
    id: string,
    payload: { toEngineerId: string; reason: string },
  ): Promise<{ message: string }> =>
    apiClient
      .post<{ message: string }>(`/inspections/${id}/ownership/transfer`, payload)
      .then((r) => r.data),

  respondToTransfer: (
    id: string,
    payload: { accept: boolean; comment?: string },
  ): Promise<{ message: string }> =>
    apiClient
      .post<{ message: string }>(`/inspections/${id}/ownership/transfer/respond`, payload)
      .then((r) => r.data),

  requestTakeover: (id: string, payload: { reason: string }): Promise<{ message: string }> =>
    apiClient
      .post<{ message: string }>(`/inspections/${id}/ownership/takeover`, payload)
      .then((r) => r.data),

  respondToTakeover: (
    id: string,
    payload: { accept: boolean; comment?: string },
  ): Promise<{ message: string }> =>
    apiClient
      .post<{ message: string }>(`/inspections/${id}/ownership/takeover/respond`, payload)
      .then((r) => r.data),

  forceTransfer: (
    id: string,
    payload: { toEngineerId: string; reason: string; comment: string },
  ): Promise<{ message: string }> =>
    apiClient
      .post<{ message: string }>(`/inspections/${id}/ownership/force-transfer`, payload)
      .then((r) => r.data),

  getOwnershipHistory: (id: string): Promise<OwnershipHistoryResponse> =>
    apiClient
      .get<OwnershipHistoryResponse>(`/inspections/${id}/ownership/history`)
      .then((r) => r.data),

  getPendingTransfers: (): Promise<PendingTransferItem[]> =>
    apiClient
      .get<PendingTransferItem[]>('/inspections/ownership/pending-transfers')
      .then((r) => r.data),

  getPendingTakeovers: (): Promise<PendingTakeoverItem[]> =>
    apiClient
      .get<PendingTakeoverItem[]>('/inspections/ownership/pending-takeovers')
      .then((r) => r.data),

  revokeTransfer: (id: string): Promise<{ message: string }> =>
    apiClient
      .post<{ message: string }>(`/inspections/${id}/ownership/transfer/revoke`)
      .then((r) => r.data),

  // ── Group 4 — As-Received / As-Found condition checks ───────────────────────
  //
  // GET is a PURE READ. An inspection created before Group 4 returns [], and is
  // never given a checklist by being opened (Owner decision D3). PUT saves
  // answers only — the checklist itself is frozen (Owner decision D4).

  getConditionChecks: (id: string): Promise<InspectionConditionCheck[]> =>
    apiClient
      .get<InspectionConditionCheck[]>(`/inspections/${id}/condition-checks`)
      .then((r) => (Array.isArray(r.data) ? r.data : [])),

  saveConditionChecks: (
    id: string,
    responses: ConditionCheckResponseInput[],
  ): Promise<InspectionConditionCheck[]> =>
    apiClient
      .put<InspectionConditionCheck[]>(`/inspections/${id}/condition-checks`, { responses })
      .then((r) => (Array.isArray(r.data) ? r.data : [])),
};

/** The job's OPEN modification round, summarised for a queue row (Stage B). */
export interface OpenModificationRoundSummary {
  roundId: string;
  /** The request's identity within the job — "Request #N". */
  roundNumber: number;
  state: string;
  /** Which party the workflow is waiting on. Ownership lives here, never on an assignee. */
  waitingFor: string | null;
  /** Newest customer-meaning request, falling back to the archival opening text. */
  latestCustomerRequestText: string | null;
}

/** Group 4 — one row of the frozen As-Received / As-Found checklist. */
export interface InspectionConditionCheck {
  id: string;
  inspectionId: string;
  /** Snapshot text, frozen when the inspection was created. Never a live lookup. */
  parameterLabel: string;
  sortOrder: number;
  /** The fixed final row. Cannot be edited, removed or reordered. */
  isOthers: boolean;
  asFoundCondition: string | null;
  status: 'OK' | 'NOT_OK' | null;
  remarks: string | null;
  recordedAt: string | null;
}

/** Answers only. There is deliberately no way to express label, order or Others. */
export interface ConditionCheckResponseInput {
  id: string;
  asFoundCondition?: string;
  status?: 'OK' | 'NOT_OK';
  remarks?: string;
}
